import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Home, Menu, Moon, Sun } from 'lucide-react';
import PublicNav, { mobileNavBtnClass } from './PublicNav';
import PublicSiteFooter from './PublicSiteFooter';

interface Props {
  darkMode: boolean;
  onToggleDarkMode: () => void;
}

const themeBtnClass = (darkMode: boolean) =>
  `p-2 rounded-lg border shrink-0 w-10 h-10 flex items-center justify-center ${
    darkMode
      ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
      : 'bg-white border-gray-200 hover:bg-gray-50'
  }`;

/** Herkese açık sayfalarda üstte sabit site menüsü */
const PublicSiteLayout: React.FC<Props> = ({ darkMode, onToggleDarkMode }) => {
  const { pathname } = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const mobileBtn = mobileNavBtnClass(darkMode);
  const hideNav =
    (pathname.startsWith('/embed/') && pathname !== '/embed-docs') ||
    pathname.startsWith('/admin') ||
    pathname === '/otomobil/login' ||
    pathname.startsWith('/otomobil/admin');

  if (hideNav) {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">
      <header
        className={`sticky top-0 z-40 border-b backdrop-blur shrink-0 ${
          darkMode ? 'bg-gray-900/95 border-gray-700' : 'bg-white/95 border-gray-200'
        }`}
      >
        <div className="w-full px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5">
          <div className="md:hidden grid grid-cols-3 items-center gap-2 min-h-[48px]">
            <div className="flex justify-start">
              <button
                type="button"
                className={mobileBtn}
                aria-expanded={mobileNavOpen}
                aria-label="Site menüsünü aç"
                onClick={() => setMobileNavOpen(true)}
              >
                <Menu size={22} />
              </button>
            </div>
            <div className="flex justify-center">
              <Link
                to="/"
                className={mobileBtn}
                aria-label="Anasayfa"
                onClick={() => setMobileNavOpen(false)}
              >
                <Home size={22} />
              </Link>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onToggleDarkMode}
                className={themeBtnClass(darkMode)}
                aria-label={darkMode ? 'Aydınlık tema' : 'Karanlık tema'}
              >
                {darkMode ? (
                  <Sun className="text-amber-500" size={20} />
                ) : (
                  <Moon className="text-indigo-600" size={20} />
                )}
              </button>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2 lg:gap-3 min-h-[48px] w-full">
            <PublicNav
              darkMode={darkMode}
              mobileOpen={mobileNavOpen}
              onMobileOpenChange={setMobileNavOpen}
              hideMobileTrigger
              className="flex-1 min-w-0 w-full"
            />
            <button
              type="button"
              onClick={onToggleDarkMode}
              className={themeBtnClass(darkMode)}
              aria-label={darkMode ? 'Aydınlık tema' : 'Karanlık tema'}
            >
              {darkMode ? (
                <Sun className="text-amber-500" size={20} />
              ) : (
                <Moon className="text-indigo-600" size={20} />
              )}
            </button>
          </div>
        </div>
      </header>
      <main id="main-content" className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
      <PublicSiteFooter darkMode={darkMode} />
    </div>
  );
};

export default PublicSiteLayout;
